import React, { useCallback, useRef, useState } from "react";
import {
  Button, ButtonGroup, Card, CardBody, CardHeader, CardTitle,
  Col, FormFeedback, FormGroup, Input, Row, Spinner,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import api from "services/api";
import { useToast } from "hooks/toast";
import { agoraInputLocal, ehUrgente } from "../../../../utils/limitesColetaEntrega";
import {
  buscarDestinatarioPorCnpj,
  buscarRuaPorCep,
  formatCnpjCpf,
  soDigitos,
} from "../../../../utils/destinatarioCotacao"; // PO1-3487

const parseNumericBR = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const cleaned = value
    .replace(/\s/g, "")
    .replace(/[R$]/gi, "")
    .replace(/[^\d.,-]/g, "");

  if (!cleaned) return null;

  let normalized = cleaned;
  if (cleaned.includes(",")) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(".")) {
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const last = parts.pop();
      normalized = `${parts.join("")}.${last}`;
    }
  }

  normalized = normalized.replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatBRL = (value) => {
  const number = parseNumericBR(value);
  if (number === null) return "";
  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNum = (value) => {
  const number = parseNumericBR(value);
  if (number === null) return "";
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCep = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const numField = (field, value, onChange) => ({
  value,
  inputMode: "decimal",
  className: "wizard-input",
  onChange: (e) => onChange(field, e.target.value),
  onBlur: () => { const f = formatNum(value); if (f) onChange(field, f); },
  placeholder: "0",
  style: { MozAppearance: "textfield" },
});

// PO1-3303 (H1): a carga pode ser informada como total (campos únicos, como
// sempre) ou por caixas (uma linha por tipo de caixa). Os totais vêm prontos
// do index (resumoCaixas) para este componente não repetir a conta.
const MODO_CAIXAS = "caixas";
const MODO_TOTAL = "total";

// PO1-3487: link "+ detalhes" dos blocos de entrega e destinatário. Fechado,
// mostra um resumo do que já foi preenchido.
const BotaoDetalhes = ({ id, aberto, onClick, resumo }) => (
  <div style={{ margin: "-4px 0 12px", fontSize: 13 }}>
    <button
      id={id}
      type="button"
      onClick={onClick}
      aria-expanded={aberto}
      style={{ background: "none", border: 0, padding: 0, color: "#1d8cf8", fontWeight: 600, cursor: "pointer" }}
    >
      {aberto ? "− ocultar detalhes" : "+ detalhes"}
    </button>
    {!aberto && resumo ? <span style={{ marginLeft: 8, opacity: 0.75 }}>{resumo}</span> : null}
  </div>
);

const Step1DadosEmbarque = ({
  formData,
  onChange,
  remetentes,
  errors = {},
  usarCidadeSemCEP = false,
  onToggleModoDestino,
  estados = [],
  modoCarga = MODO_TOTAL,
  onModoCargaChange,
  caixas = [],
  onAddCaixa,
  onRemoveCaixa,
  onCaixaChange,
  resumoCaixas = "",
}) => {
  const porCaixas = modoCarga === MODO_CAIXAS;
  const { addToast } = useToast();
  // PO1-2903 (H2): densidade é derivada de peso ÷ volume, não digitada.
  const densidadeCalculada = (() => {
    const peso = parseNumericBR(formData.peso);
    const volume = parseNumericBR(formData.volume);
    if (!peso || !volume) return "";
    return (peso / volume).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  })();
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState(null);
  const [cepPreview, setCepPreview] = useState(null);
  const [registeringCep, setRegisteringCep] = useState(false);
  // PO1-3487: consulta do destinatário pelo CNPJ.
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [cnpjAviso, setCnpjAviso] = useState(null);
  // "+ detalhes": abertos de saída só se já há algo preenchido (edição/rascunho).
  const [detalhesEntrega, setDetalhesEntrega] = useState(() =>
    Boolean(formData.entregaNomeLocal || formData.entregaLogradouro || formData.entregaNumero || formData.entregaComplemento || formData.entregaBairro)
  );
  const [detalhesDest, setDetalhesDest] = useState(() =>
    Boolean(formData.destCnpjCpf || formData.destLogradouro || formData.destCep || formData.destCidade)
  );
  const detalhesDestAbertos = detalhesDest || !!errors.destCnpjCpf;
  const resumoEntrega = [
    formData.entregaNomeLocal,
    [formData.entregaLogradouro, formData.entregaNumero].filter(Boolean).join(", "),
    formData.entregaBairro,
  ]
    .filter(Boolean)
    .join(" · ");
  const resumoDest = [
    formData.destCnpjCpf,
    [formData.destLogradouro, formData.destNumero].filter(Boolean).join(", "),
    [formData.destCidade, formData.destUf].filter(Boolean).join("/"),
  ]
    .filter(Boolean)
    .join(" · ");

  // Rua e bairro do local de entrega pelo CEP, só se ainda estão vazios.
  const completarRuaEntrega = useCallback(
    async (cep, preview) => {
      if (formData.entregaLogradouro || formData.entregaBairro) return;
      const rua = preview || (await buscarRuaPorCep(cep));
      if (!rua) return;
      if (rua.logradouro) onChange("entregaLogradouro", rua.logradouro);
      if (rua.bairro) onChange("entregaBairro", rua.bairro);
    },
    [formData.entregaLogradouro, formData.entregaBairro, onChange]
  );

  const handleCnpjBlur = useCallback(async () => {
    const digits = soDigitos(formData.destCnpjCpf);
    setCnpjAviso(null);
    if (!digits) return;
    if (digits.length !== 14) {
      if (digits.length !== 11) setCnpjAviso("Informe 14 dígitos (CNPJ) ou 11 (CPF).");
      return;
    }
    setLoadingCnpj(true);
    try {
      const d = await buscarDestinatarioPorCnpj(digits);
      if (!d) return;
      onChange("destinatario", d.razaoSocial);
      onChange("destLogradouro", d.logradouro);
      onChange("destNumero", d.numero);
      onChange("destComplemento", d.complemento);
      onChange("destBairro", d.bairro);
      onChange("destCep", d.cep);
      onChange("destCidade", d.cidade);
      onChange("destUf", d.uf);
      // Caso comum: entrega no endereço do destinatário. Só preenche o local
      // de entrega se ele ainda está vazio; para entregar em outro lugar, o
      // usuário troca o CEP de entrega.
      if (!soDigitos(formData.cep) && !formData.cidade && d.cep) {
        onChange("cep", d.cep);
        onChange("cidade", d.cidade);
        onChange("uf", d.uf);
        if (!formData.entregaLogradouro) {
          onChange("entregaLogradouro", d.logradouro);
          onChange("entregaNumero", d.numero);
          onChange("entregaComplemento", d.complemento);
          onChange("entregaBairro", d.bairro);
        }
      }
    } catch (err) {
      setCnpjAviso("CNPJ não encontrado na Receita. Preencha os dados à mão.");
    } finally {
      setLoadingCnpj(false);
    }
  }, [formData.destCnpjCpf, formData.cep, formData.cidade, formData.entregaLogradouro, onChange]);
  const debounceTimerRef = useRef(null);

  const handleCepBlur = useCallback(async () => {
    const digits = String(formData.cep || "").replace(/\D/g, "");
    if (digits.length !== 8) return;

    setLoadingCep(true);
    setCepError(null);
    setCepPreview(null);
    try {
      const res = await api.get(`/endereco/cep/${digits}`);
      onChange("cidade", res.data.cidade || "");
      onChange("uf", res.data.uf || "");
      await completarRuaEntrega(digits); // PO1-3487
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        try {
          const preview = await api.get(`/endereco/cep/${digits}/preview`);
          setCepPreview(preview.data);
          await completarRuaEntrega(digits, preview.data); // PO1-3487
        } catch (previewErr) {
          const msg = previewErr?.response?.data?.error || "CEP não encontrado";
          setCepError(msg);
        }
      } else {
        setCepError("Erro ao consultar CEP");
      }
    } finally {
      setLoadingCep(false);
    }
  }, [formData.cep, onChange, completarRuaEntrega]);

  const handleCadastrarCep = useCallback(async () => {
    if (!cepPreview) return;
    setRegisteringCep(true);
    try {
      const res = await api.post(`/endereco/cep/${cepPreview.cep}/cadastrar`);
      onChange("cidade", res.data.cidade || "");
      onChange("uf", res.data.uf || "");
      setCepPreview(null);
      setCepError(null);
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "CEP cadastrado",
        description: `${res.data.cidade}/${res.data.uf}`,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || "Não foi possível cadastrar o CEP";
      setCepError(msg);
      setCepPreview(null);
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro ao cadastrar CEP",
        description: msg,
      });
    } finally {
      setRegisteringCep(false);
    }
  }, [cepPreview, onChange, addToast]);

  const handleUsarCidadeManual = useCallback(() => {
    setCepPreview(null);
    setCepError(null);
    if (onToggleModoDestino) onToggleModoDestino(true);
  }, [onToggleModoDestino]);

  const loadCidadeOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        if (!formData.uf) {
          resolve([]);
          return;
        }
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const res = await api.get("/cidades", {
              params: { uf: formData.uf, q: inputValue || "" },
            });
            const options = (res.data || []).map((c) => ({
              value: c.id,
              label: c.nome,
              uf: c.uf,
            }));
            resolve(options);
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    [formData.uf],
  );

  const cidadeValue = formData.idCidadeDestino
    ? { value: formData.idCidadeDestino, label: formData.cidade, uf: formData.uf }
    : null;

  const ufValue = formData.uf
    ? estados.find((e) => e.value === formData.uf) || {
        value: formData.uf,
        label: formData.uf,
      }
    : null;

  const remetenteValue =
    remetentes.find(
      (r) => String(r.value) === String(formData.remetente?.value),
    ) || formData.remetente || null;

  return (
    <>
      <Card className="wizard-card">
        <CardHeader className="wizard-card-header-with-toggle">
          <CardTitle tag="h6" className="wizard-card-title-inline">
            Local de entrega e destinatário
          </CardTitle>
          {onToggleModoDestino && (
            <ButtonGroup size="sm" className="wizard-modo-destino-toggle">
              <Button
                color="primary"
                outline={usarCidadeSemCEP}
                active={!usarCidadeSemCEP}
                onClick={() => onToggleModoDestino(false)}
                type="button"
              >
                Buscar por CEP
              </Button>
              <Button
                color="primary"
                outline={!usarCidadeSemCEP}
                active={usarCidadeSemCEP}
                onClick={() => onToggleModoDestino(true)}
                type="button"
              >
                Buscar por Cidade
              </Button>
            </ButtonGroup>
          )}
        </CardHeader>
        <CardBody>
          {/* PO1-3487: local de entrega primeiro, só CEP/cidade/UF como hoje; "+ detalhes"
              abre nome do local, rua, número, complemento e bairro. O frete é calculado por ele. */}
          <div className="wizard-label" style={{ fontWeight: 700, marginBottom: 6 }}>
            Local de entrega <span style={{ fontWeight: 400 }}>(o frete é calculado por ele)</span>
          </div>
          {usarCidadeSemCEP ? (
            <Row>
              <Col md="3">
                <FormGroup>
                  <label className="wizard-label">UF *</label>
                  <Select
                    className="wizard-select"
                    classNamePrefix="wizard-select"
                    options={estados}
                    value={ufValue}
                    onChange={(opt) => {
                      onChange("uf", opt?.value || "");
                      onChange("cidade", "");
                      onChange("idCidadeDestino", null);
                    }}
                    placeholder="UF"
                    isClearable={false}
                  />
                  {errors.uf && (
                    <FormFeedback style={{ display: "block" }}>
                      {errors.uf}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col md="9">
                <FormGroup>
                  <label className="wizard-label">Cidade *</label>
                  <AsyncSelect
                    className="wizard-select"
                    classNamePrefix="wizard-select"
                    cacheOptions
                    defaultOptions
                    loadOptions={loadCidadeOptions}
                    value={cidadeValue}
                    onChange={(opt) => {
                      onChange("idCidadeDestino", opt?.value || null);
                      onChange("cidade", opt?.label || "");
                      if (opt?.uf) onChange("uf", opt.uf);
                    }}
                    placeholder={formData.uf ? "Digite para buscar..." : "Selecione a UF primeiro"}
                    isClearable
                    isDisabled={!formData.uf}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "Nenhuma cidade encontrada" : "Digite para buscar"
                    }
                    loadingMessage={() => "Buscando..."}
                  />
                  {errors.cidade && (
                    <FormFeedback style={{ display: "block" }}>
                      {errors.cidade}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md="4">
                <FormGroup>
                  <label className="wizard-label">CEP *</label>
                  <div style={{ position: "relative" }}>
                    <Input
                      className="wizard-input"
                      value={formData.cep}
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={9}
                      onChange={(e) => {
                        onChange("cep", formatCep(e.target.value));
                        setCepError(null);
                        setCepPreview(null);
                      }}
                      onBlur={handleCepBlur}
                      placeholder="00000-000"
                      invalid={!!errors.cep || !!cepError}
                    />
                    {loadingCep && (
                      <Spinner
                        size="sm"
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#1d8cf8" }}
                      />
                    )}
                  </div>
                  {(errors.cep || cepError) && (
                    <FormFeedback style={{ display: "block" }}>
                      {errors.cep || cepError}
                    </FormFeedback>
                  )}
                  {cepPreview && (
                    <div
                      className="cep-preview-card"
                      style={{
                        marginTop: 8,
                        padding: 12,
                        border: "1px solid #1d8cf8",
                        borderRadius: 6,
                        background: "rgba(29, 140, 248, 0.08)",
                      }}
                    >
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        <strong>CEP {formatCep(cepPreview.cep)}</strong> não está cadastrado.
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        Encontramos: <strong>{cepPreview.cidade}/{cepPreview.uf}</strong>
                      </div>
                      {cepPreview.logradouro && (
                        <div style={{ fontSize: 12, color: "#9a9a9a" }}>
                          {cepPreview.logradouro}
                          {cepPreview.bairro ? `, ${cepPreview.bairro}` : ""}
                        </div>
                      )}
                      {cepPreview.exato === false && (
                        <div style={{ fontSize: 12, color: "#ff8d72", marginTop: 6 }}>
                          ⚠ Encontramos uma região próxima (CEP {formatCep(cepPreview.cepRetornado)}). Confirme antes de cadastrar.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <Button
                          color="primary"
                          size="sm"
                          type="button"
                          onClick={handleCadastrarCep}
                          disabled={registeringCep}
                        >
                          {registeringCep ? "Cadastrando..." : "Cadastrar e usar"}
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          outline
                          type="button"
                          onClick={handleUsarCidadeManual}
                          disabled={registeringCep}
                        >
                          Usar Cidade/UF manualmente
                        </Button>
                      </div>
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md="5">
                <FormGroup>
                  <label className="wizard-label">Cidade</label>
                  <Input
                    className="wizard-input"
                    value={formData.cidade}
                    onChange={(e) => onChange("cidade", e.target.value)}
                    placeholder="Preenchida pelo CEP"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <label className="wizard-label">UF</label>
                  <Input
                    className="wizard-input"
                    value={formData.uf}
                    onChange={(e) => onChange("uf", e.target.value)}
                    placeholder="UF"
                    maxLength={2}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          <BotaoDetalhes
            id="cotacao-entrega-detalhes"
            aberto={detalhesEntrega}
            onClick={() => setDetalhesEntrega((v) => !v)}
            resumo={resumoEntrega}
          />
          {detalhesEntrega ? (
            <>
            <Row>
              <Col md="4">
                <FormGroup>
                  <label className="wizard-label" htmlFor="cotacao-entrega-nome">Nome do local</label>
                  <Input id="cotacao-entrega-nome" className="wizard-input" value={formData.entregaNomeLocal || ""} onChange={(e) => onChange("entregaNomeLocal", e.target.value)} placeholder="Ex.: CD Campinas" />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="wizard-label" htmlFor="cotacao-entrega-rua">Rua</label>
                  <Input id="cotacao-entrega-rua" className="wizard-input" value={formData.entregaLogradouro || ""} onChange={(e) => onChange("entregaLogradouro", e.target.value)} placeholder="Preenchida pelo CEP quando houver" />
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup>
                  <label className="wizard-label">Número</label>
                  <Input className="wizard-input" value={formData.entregaNumero || ""} onChange={(e) => onChange("entregaNumero", e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <label className="wizard-label">Complemento</label>
                  <Input className="wizard-input" value={formData.entregaComplemento || ""} onChange={(e) => onChange("entregaComplemento", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="wizard-label">Bairro</label>
                  <Input className="wizard-input" value={formData.entregaBairro || ""} onChange={(e) => onChange("entregaBairro", e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
            </>
          ) : null}

          {/* PO1-3487: destinatário da NF. Só o nome, como hoje; "+ detalhes" abre CNPJ/CPF
              (consulta a Receita) e endereço. Só telas internas: a transportadora não vê. */}
          <FormGroup style={{ marginTop: 8 }}>
            <label className="wizard-label" htmlFor="cotacao-dest-razao">Destinatário</label>
            <Input
              id="cotacao-dest-razao"
              className="wizard-input"
              value={formData.destinatario}
              onChange={(e) => onChange("destinatario", e.target.value)}
              placeholder="Nome do destinatário"
            />
          </FormGroup>
          <BotaoDetalhes
            id="cotacao-dest-detalhes"
            aberto={detalhesDestAbertos}
            onClick={() => setDetalhesDest((v) => !v)}
            resumo={resumoDest}
          />
          {detalhesDestAbertos ? (
            <>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <label className="wizard-label" htmlFor="cotacao-dest-cnpj">CNPJ/CPF</label>
                    <div style={{ position: "relative" }}>
                      <Input
                        id="cotacao-dest-cnpj"
                        className="wizard-input"
                        value={formData.destCnpjCpf || ""}
                        inputMode="numeric"
                        maxLength={18}
                        onChange={(e) => {
                          onChange("destCnpjCpf", formatCnpjCpf(e.target.value));
                          setCnpjAviso(null);
                        }}
                        onBlur={handleCnpjBlur}
                        placeholder="Busca os dados na Receita"
                        invalid={!!errors.destCnpjCpf}
                      />
                      {loadingCnpj && (
                        <Spinner
                          size="sm"
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#1d8cf8" }}
                        />
                      )}
                    </div>
                    {errors.destCnpjCpf || cnpjAviso ? (
                      <FormFeedback style={{ display: "block" }}>{errors.destCnpjCpf || cnpjAviso}</FormFeedback>
                    ) : null}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="wizard-label">Endereço</label>
                    <Input className="wizard-input" value={formData.destLogradouro || ""} onChange={(e) => onChange("destLogradouro", e.target.value)} placeholder="Rua" />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="wizard-label">Número</label>
                    <Input className="wizard-input" value={formData.destNumero || ""} onChange={(e) => onChange("destNumero", e.target.value)} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <label className="wizard-label">Bairro</label>
                    <Input className="wizard-input" value={formData.destBairro || ""} onChange={(e) => onChange("destBairro", e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="wizard-label">CEP</label>
                    <Input className="wizard-input" value={formData.destCep || ""} inputMode="numeric" maxLength={9} onChange={(e) => onChange("destCep", formatCep(e.target.value))} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label className="wizard-label">Cidade</label>
                    <Input className="wizard-input" value={formData.destCidade || ""} onChange={(e) => onChange("destCidade", e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup>
                    <label className="wizard-label">UF</label>
                    <Input className="wizard-input" value={formData.destUf || ""} maxLength={2} onChange={(e) => onChange("destUf", e.target.value.toUpperCase())} />
                  </FormGroup>
                </Col>
              </Row>
            </>
          ) : null}
        </CardBody>
      </Card>

      <Card className="wizard-card">
        <CardHeader>
          <CardTitle tag="h6">Embarque</CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Número do Embarque</label>
                <Input
                  className="wizard-input"
                  value={formData.numeroNota}
                  onChange={(e) => onChange("numeroNota", e.target.value)}
                  placeholder="Gerado se vazio"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Nº do pedido</label>
                <Input
                  className="wizard-input"
                  value={formData.codigoPedido}
                  maxLength={20}
                  onChange={(e) => onChange("codigoPedido", e.target.value)}
                  placeholder="Ex.: 26163"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Valor da Mercadoria *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  className="wizard-input"
                  value={formData.valorNF}
                  onChange={(e) => {
                    // Permite digitar livremente; formata apenas no blur
                    onChange("valorNF", e.target.value);
                  }}
                  onBlur={() => {
                    const formatted = formatBRL(parseNumericBR(formData.valorNF));
                    if (formatted) onChange("valorNF", formatted);
                  }}
                  placeholder="R$ 0,00"
                  invalid={!!errors.valorNF}
                />
                <FormFeedback>{errors.valorNF}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <label className="wizard-label">Remetente</label>
            {remetentes.length > 1 ? (
              <Select
                className="wizard-select"
                classNamePrefix="wizard-select"
                options={remetentes}
                value={remetenteValue}
                onChange={(opt) => onChange("remetente", opt)}
                placeholder="Selecione o remetente"
                isClearable={false}
              />
            ) : (
              <Input
                className="wizard-input"
                value={remetentes[0]?.label || "—"}
                readOnly
              />
            )}
          </FormGroup>
        </CardBody>
      </Card>

      <Card className="wizard-card">
        <CardHeader>
          <CardTitle tag="h6">Peso e Volume</CardTitle>
          <div className="carga-modo" role="group" aria-label="Como informar a carga">
            <button
              type="button"
              className={`carga-modo-btn${porCaixas ? "" : " active"}`}
              onClick={() => onModoCargaChange && onModoCargaChange(MODO_TOTAL)}
            >
              Carga total
            </button>
            <button
              type="button"
              className={`carga-modo-btn${porCaixas ? " active" : ""}`}
              onClick={() => onModoCargaChange && onModoCargaChange(MODO_CAIXAS)}
            >
              Por volumes
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {porCaixas && (
            <>
              <div className="info-note" style={{ marginBottom: 12 }}>
                Informe cada tipo de volume numa linha (caixa, kit, fardo, saco, palete...): quantidade, peso de <strong>um</strong>{" "}
                volume e medidas. Volumes iguais entram numa linha só. Peso total e m³ são
                calculados a partir da lista.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="vehicles-table caixas-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th style={{ width: 90 }}>Qtd</th>
                      <th>Peso por volume (kg)</th>
                      <th>Altura (cm)</th>
                      <th>Largura (cm)</th>
                      <th>Comprimento (cm)</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {caixas.map((c, i) => (
                      <tr key={c.id}>
                        <td>
                          <span className="vehicle-index-badge">{i + 1}</span>
                        </td>
                        <td>
                          <Input
                            type="text"
                            inputMode="numeric"
                            className="wizard-input"
                            value={c.quantidade}
                            onChange={(e) =>
                              onCaixaChange(c.id, "quantidade", e.target.value.replace(/\D/g, ""))
                            }
                            onBlur={() => {
                              if (!c.quantidade) onCaixaChange(c.id, "quantidade", "1");
                            }}
                          />
                        </td>
                        {["peso", "altura", "largura", "comprimento"].map((campo) => (
                          <td key={campo}>
                            <Input
                              type="text"
                              inputMode="decimal"
                              className="wizard-input"
                              placeholder="0"
                              value={c[campo]}
                              onChange={(e) => onCaixaChange(c.id, campo, e.target.value)}
                            />
                          </td>
                        ))}
                        <td>
                          {caixas.length > 1 && (
                            <button
                              type="button"
                              className="caixas-remover"
                              onClick={() => onRemoveCaixa(c.id)}
                              title="Remover volume"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="caixas-totais">
                <button type="button" className="carga-modo-btn" onClick={onAddCaixa}>
                  + Adicionar volume
                </button>
                <span>
                  Total: <strong>{resumoCaixas || "—"}</strong>
                </span>
              </div>
              {errors.caixas && <div className="caixas-erro text-danger">{errors.caixas}</div>}
            </>
          )}
          {!porCaixas && (
          <>
          <Row>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Peso Total (kg) *</label>
                <Input {...numField("peso", formData.peso, onChange)} invalid={!!errors.peso} />
                <FormFeedback>{errors.peso}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                {/* PO1-2903 (H2): este é o campo que alimenta a cubagem
                    (m³ × fator da tabela). O rótulo antigo não deixava claro
                    que ele era opcional e que sem ele o frete saía R$ 0,00. */}
                <label className="wizard-label">Volume (m³)</label>
                <Input {...numField("volume", formData.volume, onChange)} />
                <small className="text-muted">
                  Opcional. Sem volume, a cotação usa o peso real.
                </small>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                {/* Densidade é informação DERIVADA (peso ÷ volume), não entrada:
                    quem digitava aqui achava que informava m³. Somente leitura. */}
                <label className="wizard-label">Densidade (kg/m³)</label>
                <Input
                  value={densidadeCalculada}
                  readOnly
                  disabled
                  placeholder="—"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Largura (cm)</label>
                <Input {...numField("largura", formData.largura, onChange)} />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Altura (cm)</label>
                <Input {...numField("altura", formData.altura, onChange)} />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label className="wizard-label">Comprimento (cm)</label>
                <Input {...numField("comprimento", formData.comprimento, onChange)} />
              </FormGroup>
            </Col>
          </Row>
          </>
          )}

          {/* PO1-3481: limites opcionais; coleta nas próximas 24 h = urgente. */}
          <Row style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--everlog-card-border-soft, rgba(255, 255, 255, 0.08))" }}>
            <Col md="6">
              <FormGroup>
                <label className="wizard-label" htmlFor="cotacao-coletar-ate">Coletar até</label>
                <Input
                  id="cotacao-coletar-ate"
                  type="datetime-local"
                  className="wizard-input"
                  value={formData.coletarAte || ""}
                  min={agoraInputLocal()}
                  onChange={(e) => onChange("coletarAte", e.target.value)}
                  invalid={!!errors.coletarAte}
                />
                <FormFeedback>{errors.coletarAte}</FormFeedback>
                {!errors.coletarAte && ehUrgente(formData.coletarAte) ? (
                  <small className="text-warning" style={{ fontWeight: 600 }}>
                    Urgente: coleta nas próximas 24 horas. O leilão sai marcado como urgente.
                  </small>
                ) : null}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label className="wizard-label" htmlFor="cotacao-entregar-ate">Entregar até</label>
                <Input
                  id="cotacao-entregar-ate"
                  type="datetime-local"
                  className="wizard-input"
                  value={formData.entregarAte || ""}
                  min={formData.coletarAte || agoraInputLocal()}
                  onChange={(e) => onChange("entregarAte", e.target.value)}
                  invalid={!!errors.entregarAte}
                />
                <FormFeedback>{errors.entregarAte}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <label className="wizard-label">Observações</label>
            <Input
              type="textarea"
              className="wizard-input"
              value={formData.observacoes}
              onChange={(e) => onChange("observacoes", e.target.value)}
              placeholder="Informações adicionais..."
              rows={3}
              style={{ resize: "none" }}
            />
          </FormGroup>
        </CardBody>
      </Card>
    </>
  );
};

export default Step1DadosEmbarque;
